import type { ElementType, ReactNode } from 'react';
import type { ButtonProps } from '../../../Button/Button';
import type { LinkButtonProps } from '../../../LinkButton/LinkButton';
import type { TooltipPlacement } from '../../../Tooltip/Tooltip';

import { use, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../../../lib/helpers';

import Button from '../../../Button/Button';
import LinkButton from '../../../LinkButton/LinkButton';
import Tooltip from '../../../Tooltip/Tooltip';
import { TopbarContext } from '../../Topbar';

export interface TopbarButtonBaseProps {
  /**
   * Button label
   */
  label?: ReactNode;
  /**
   * If button is disabled
   */
  disabled?: boolean;
  /**
   * Tooltip text
   */
  tooltip?: string;
  /**
   * Tooltip placement
   */
  tooltipPlacement?: TooltipPlacement;
  /**
   * Phone tooltip placement
   */
  phoneTooltipPlacement?: TooltipPlacement;
  /**
   * If action is disallowed
   */
  actionDisallowed?: boolean;
  /**
   * Additional classes for button
   */
  additionalClasses?: string;
  /**
   * Additional classes for tooltip
   */
  additionalTooltipClasses?: string;
  /**
   * Test id suffix for button
   */
  testIdSuffix?: string;
}

type TopbarButtonProps<T extends ElementType> =
  | (TopbarButtonBaseProps &
      Omit<LinkButtonProps, keyof TopbarButtonBaseProps> & {
        /**
         * Item link
         */
        link: string;
      })
  | (TopbarButtonBaseProps &
      Omit<ButtonProps<T>, keyof TopbarButtonBaseProps> & {
        /**
         * Item link
         */
        link?: never;
      });

export const TopbarButton = <T extends ElementType>({
  label,
  disabled = false,
  tooltip,
  tooltipPlacement,
  phoneTooltipPlacement,
  actionDisallowed,
  additionalClasses,
  additionalTooltipClasses,
  testIdSuffix,
  ...props
}: TopbarButtonProps<T>) => {
  const { t } = useTranslation();

  const { buttonsDisabled, testId } = use(TopbarContext);
  const isDisabled = actionDisallowed || disabled || buttonsDisabled;

  const baseProps = useMemo(
    () => ({
      buttonSize: 'base' as const,
      additionalClasses,
      additionalChildrenClasses:
        'max-w-32 xs:max-w-56 md:max-w-32 lg:max-w-56 2xl:max-w-[450px]',
      title: typeof label === 'string' && label?.length > 90 ? label : '',
      disabled: isDisabled,
      iconPosition: 'start' as const,
    }),
    [label, additionalClasses, isDisabled],
  );

  const children = label ? (
    <div className="whitespace-nowrap truncate">{label}</div>
  ) : null;

  return (
    <Tooltip
      tooltip={actionDisallowed ? t('UserRoles.CannotPerformAction') : tooltip}
      tooltipPlacement={tooltipPlacement || 'bottomRight'}
      phoneTooltipPlacement={phoneTooltipPlacement || 'topCenter'}
      additionalTooltipClasses={additionalTooltipClasses}
      {...getTestProps(testId, `${testIdSuffix || label}`, 'testId')}
      {...props}
    >
      {props.link ? (
        <LinkButton
          {...baseProps}
          {...getTestProps(testId, `${testIdSuffix || label}`, 'testId')}
          {...(props as Omit<LinkButtonProps, keyof TopbarButtonBaseProps>)}
        >
          {children}
        </LinkButton>
      ) : (
        <Button
          {...baseProps}
          {...getTestProps(testId, `${testIdSuffix || label}`, 'testId')}
          {...(props as Omit<ButtonProps, keyof TopbarButtonBaseProps>)}
        >
          {children}
        </Button>
      )}
    </Tooltip>
  );
};

export default TopbarButton;
